import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Canvas,
  Circle,
  FabricImage,
  type FabricObject,
  IText,
  Line,
  Point,
  Rect,
  Triangle,
  type TPointerEventInfo,
} from 'fabric';

type AnnotationTool = 'select' | 'pencil' | 'line' | 'arrow' | 'rect' | 'ellipse' | 'text';

type UseFabricAnnotationCanvasOptions = {
  imageUrl: string;
};

/**
 * Fabric `Canvas` moves the element into `[data-fabric="wrapper"]`. That wrapper's
 * `clientWidth`/`clientHeight` match Fabric's set pixel size (often the initial
 * 300×150), not the React layout container — so measure the wrapper's parent instead.
 */
function getCanvasLayoutContainer(canvasElement: HTMLCanvasElement): HTMLElement | null {
  const fabricWrapper = canvasElement.closest('[data-fabric="wrapper"]');
  if (fabricWrapper?.parentElement) {
    return fabricWrapper.parentElement;
  }
  return canvasElement.parentElement;
}

/** Extract screen-space coordinates from a Fabric native event (MouseEvent, PointerEvent, or TouchEvent). */
function getClientPoint(nativeEvent: Event): { x: number; y: number } {
  if ('clientX' in nativeEvent) {
    return { x: (nativeEvent as MouseEvent).clientX, y: (nativeEvent as MouseEvent).clientY };
  }
  const touch =
    (nativeEvent as TouchEvent).touches[0] ?? (nativeEvent as TouchEvent).changedTouches[0];
  return { x: touch?.clientX ?? 0, y: touch?.clientY ?? 0 };
}

/** Prefer `scenePoint` from Fabric's event payload — it matches internal drawing and avoids stale pointer cache. */
function scenePointFromFabricEvent(
  fabricCanvas: Canvas,
  options: Pick<TPointerEventInfo, 'scenePoint' | 'e'>
): Point {
  return options.scenePoint ?? fabricCanvas.getScenePoint(options.e);
}

const LAYOUT_RESIZE_DEBOUNCE_MS = 200;
const HISTORY_MAX_SNAPSHOTS = 50;
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 8;
const PENCIL_STROKE_WIDTH = 3;

const DEFAULT_ANNOTATION_COLOR = '#e53935';

/** Keep the background raster from being picked / transformed in select mode (re-applied after history loads). */
const BASE_RASTER_LOCKS = {
  selectable: false,
  evented: false,
  hasControls: false,
  hasBorders: false,
  lockMovementX: true,
  lockMovementY: true,
  lockScalingX: true,
  lockScalingY: true,
  lockRotation: true,
  excludeFromExport: false,
} as const;

function applyBaseRasterLocks(fabricCanvas: Canvas, baseImageRef: { current: FabricImage | null }) {
  const objects = fabricCanvas.getObjects();
  const fromReference =
    baseImageRef.current &&
    objects.includes(baseImageRef.current) &&
    baseImageRef.current.type === 'image'
      ? baseImageRef.current
      : null;
  const raster = fromReference ?? objects.find((object) => object.type === 'image');
  if (!raster || raster.type !== 'image') {
    return;
  }
  raster.set({ ...BASE_RASTER_LOCKS });
  baseImageRef.current = raster as FabricImage;
}

/**
 * Match Analysis `Image` (`crossOrigin="use-credentials"`) and `urlToBase64` (`credentials: 'include'`).
 * `anonymous` breaks signed CloudFront. A `fetch` + blob URL keeps the canvas exportable when credentialed GET works.
 */
async function loadFabricImageForAnnotation(cdnUrl: string): Promise<FabricImage> {
  try {
    return await FabricImage.fromURL(cdnUrl, { crossOrigin: 'use-credentials' });
  } catch {
    // Fall through — e.g. strict CORS on `<img>` while `fetch` still succeeds with cookies.
  }

  const response = await fetch(cdnUrl, { credentials: 'include', mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch image (${String(response.status)})`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await FabricImage.fromURL(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export const useFabricAnnotationCanvas = ({ imageUrl }: UseFabricAnnotationCanvasOptions) => {
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);
  const fabricCanvasRef = useRef<Canvas | null>(null);
  /** Raster base layer (not `backgroundImage`) so we can scale it to fill the viewport reliably. */
  const baseImageRef = useRef<FabricImage | null>(null);
  const setViewportToFitRef = useRef<() => void>(() => undefined);
  const setToolRef = useRef<(tool: AnnotationTool) => void>((_tool: AnnotationTool) => undefined);
  const activeToolRef = useRef<AnnotationTool>('select');
  /** Read by drawing handlers (mounted once); keep in sync via `setAnnotationColor`. */
  const annotationColorRef = useRef(DEFAULT_ANNOTATION_COLOR);
  /** Avoid refitting the base image while the user is drawing — it shifts content under strokes. */
  const pointerDownOnCanvasRef = useRef(false);
  const deferredLayoutRefitRef = useRef(false);
  const lastAppliedLayoutSizeRef = useRef({ width: 0, height: 0 });
  const layoutResizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const historyRef = useRef<{ snapshots: object[]; index: number; isApplying: boolean }>({
    snapshots: [],
    index: -1,
    isApplying: false,
  });
  const drawingStateRef = useRef<{
    isDrawing: boolean;
    object: FabricObject | null;
    startX: number;
    startY: number;
  }>({ isDrawing: false, object: null, startX: 0, startY: 0 });
  const panStateRef = useRef<{ isPanning: boolean; lastX: number; lastY: number }>({
    isPanning: false,
    lastX: 0,
    lastY: 0,
  });

  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [activeTool, setActiveTool] = useState<AnnotationTool>('select');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [pickedAnnotationColor, setPickedAnnotationColor] = useState(DEFAULT_ANNOTATION_COLOR);

  const setCanvasElement = useCallback((node: HTMLCanvasElement | null) => {
    canvasElementRef.current = node;
  }, []);

  const updateUndoRedoState = useCallback(() => {
    const { snapshots, index } = historyRef.current;
    setCanUndo(index > 0);
    setCanRedo(index >= 0 && index < snapshots.length - 1);
  }, []);

  const pushHistorySnapshot = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;
    if (historyRef.current.isApplying) return;

    const snapshot = fabricCanvas.toJSON();
    const { index } = historyRef.current;

    const nextSnapshots = historyRef.current.snapshots.slice(0, index + 1);
    nextSnapshots.push(snapshot);

    // Cap history depth to reduce memory pressure.
    const cappedSnapshots =
      nextSnapshots.length > HISTORY_MAX_SNAPSHOTS
        ? nextSnapshots.slice(nextSnapshots.length - HISTORY_MAX_SNAPSHOTS)
        : nextSnapshots;

    historyRef.current.snapshots = cappedSnapshots;
    historyRef.current.index = cappedSnapshots.length - 1;
    updateUndoRedoState();
  }, [updateUndoRedoState]);

  const setAnnotationColor = useCallback(
    (color: string) => {
      annotationColorRef.current = color;
      setPickedAnnotationColor(color);
      const fabricCanvas = fabricCanvasRef.current;
      const brush = fabricCanvas?.freeDrawingBrush;
      if (brush) {
        brush.color = color;
      }
      if (!fabricCanvas) return;

      const active = fabricCanvas.getActiveObject();
      const base = baseImageRef.current;
      if (
        !active ||
        active === base ||
        active.type === 'activeSelection' ||
        activeToolRef.current !== 'select'
      ) {
        return;
      }

      const objectType = active.type;
      if (objectType === 'i-text' || objectType === 'textbox' || objectType === 'text') {
        active.set('fill', color);
      } else {
        active.set({ stroke: color });
        if (objectType === 'triangle') {
          active.set({ fill: color });
        }
      }
      active.setCoords();
      fabricCanvas.requestRenderAll();
      pushHistorySnapshot();
    },
    [pushHistorySnapshot]
  );

  useEffect(() => {
    const element = canvasElementRef.current;
    if (!element) return;

    const fabricCanvas = new Canvas(element, {
      preserveObjectStacking: true,
      selection: true,
      // Retina backing-store scaling often misaligns pointers when the canvas sits in flex/transform layouts (e.g. split panel).
      enableRetinaScaling: false,
      defaultCursor: 'grab',
    });
    fabricCanvasRef.current = fabricCanvas;

    let isDisposed = false;

    const load = async () => {
      try {
        setLoadError(null);

        const image = await loadFabricImageForAnnotation(imageUrl);
        if (isDisposed) return;

        image.set({ ...BASE_RASTER_LOCKS });

        baseImageRef.current = image;
        fabricCanvas.add(image);
        fabricCanvas.sendObjectToBack(image);
        fabricCanvas.requestRenderAll();
        setIsReady(true);

        historyRef.current.snapshots = [];
        historyRef.current.index = -1;

        // Fit after the browser has decoded bitmap dimensions (otherwise naturalWidth can be 0).
        const scheduleFitWhenDecoded = () => {
          const applyFit = () => {
            requestAnimationFrame(() => setViewportToFitRef.current());
          };
          const elementSource = image.getElement();
          if (elementSource instanceof HTMLImageElement) {
            if (elementSource.complete && elementSource.naturalWidth > 0) {
              applyFit();
            } else {
              elementSource.addEventListener('load', applyFit, { once: true });
              elementSource.addEventListener('error', applyFit, { once: true });
            }
          } else {
            applyFit();
          }
        };
        scheduleFitWhenDecoded();
      } catch (error) {
        if (!isDisposed) {
          setIsReady(false);
          const baseMessage = error instanceof Error ? error.message : 'Failed to load image';
          setLoadError(
            `${baseMessage} If other assembly thumbnails load, try reloading after cookies refresh; otherwise the CDN may be blocking credentialed image requests from this app.`
          );
        }
      }
    };

    void load();

    const ensureBaseImageAtBottom = () => {
      const objects = fabricCanvas.getObjects();
      const fromReference =
        baseImageRef.current &&
        objects.includes(baseImageRef.current) &&
        baseImageRef.current.type === 'image'
          ? baseImageRef.current
          : null;
      const rasterFromBack = objects.find((object) => object.type === 'image') ?? null;
      const raster = fromReference ?? rasterFromBack;
      if (!raster || raster.type !== 'image') return;
      fabricCanvas.sendObjectToBack(raster);
      applyBaseRasterLocks(fabricCanvas, baseImageRef);
    };

    const onObjectChange = () => pushHistorySnapshot();
    const onObjectAdded = (event: { target: FabricObject }) => {
      ensureBaseImageAtBottom();
      if (event.target === baseImageRef.current) return;
      pushHistorySnapshot();
    };
    const onPathCreated = () => {
      if (historyRef.current.isApplying) return;
      // `object:added` already snapshots and sends the base image to the back.
      // Exit free-draw so the next click does not paint a dot; keeps toolbar and Fabric in sync.
      queueMicrotask(() => setToolRef.current('select'));
    };

    fabricCanvas.on('object:added', onObjectAdded);
    fabricCanvas.on('object:modified', onObjectChange);
    fabricCanvas.on('object:removed', onObjectChange);
    fabricCanvas.on('path:created', onPathCreated);

    const flushDeferredLayoutRefit = () => {
      if (!deferredLayoutRefitRef.current) return;
      deferredLayoutRefitRef.current = false;
      const el = canvasElementRef.current;
      const layout = el ? getCanvasLayoutContainer(el) : null;
      if (layout) {
        const width = Math.round(layout.clientWidth);
        const height = Math.round(layout.clientHeight);
        const last = lastAppliedLayoutSizeRef.current;
        if (width === last.width && height === last.height) {
          return;
        }
      }
      requestAnimationFrame(() => setViewportToFitRef.current());
    };

    const onCanvasPointerDown = () => {
      pointerDownOnCanvasRef.current = true;
    };

    const onCanvasPointerUp = () => {
      pointerDownOnCanvasRef.current = false;
      flushDeferredLayoutRefit();
    };

    const onWindowPointerUp = () => {
      if (!pointerDownOnCanvasRef.current) return;
      onCanvasPointerUp();
    };

    fabricCanvas.on('mouse:down', onCanvasPointerDown);
    fabricCanvas.on('mouse:up', onCanvasPointerUp);
    // Release outside the canvas (e.g. drag-to-draw) must still end interaction.
    globalThis.addEventListener('pointerup', onWindowPointerUp);
    globalThis.addEventListener('pointercancel', onWindowPointerUp);

    return () => {
      isDisposed = true;
      setIsReady(false);
      setLoadError(null);
      lastAppliedLayoutSizeRef.current = { width: 0, height: 0 };
      if (layoutResizeTimerRef.current !== null) {
        clearTimeout(layoutResizeTimerRef.current);
        layoutResizeTimerRef.current = null;
      }
      pointerDownOnCanvasRef.current = false;
      deferredLayoutRefitRef.current = false;
      fabricCanvas.off('object:added', onObjectAdded);
      fabricCanvas.off('object:modified', onObjectChange);
      fabricCanvas.off('object:removed', onObjectChange);
      fabricCanvas.off('path:created', onPathCreated);
      fabricCanvas.off('mouse:down', onCanvasPointerDown);
      fabricCanvas.off('mouse:up', onCanvasPointerUp);
      globalThis.removeEventListener('pointerup', onWindowPointerUp);
      globalThis.removeEventListener('pointercancel', onWindowPointerUp);
      fabricCanvas.dispose();
      fabricCanvasRef.current = null;
      baseImageRef.current = null;
    };
  }, [imageUrl, pushHistorySnapshot]);

  const syncBaseImageRefAfterLoad = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas || fabricCanvas.getObjects().length === 0) {
      baseImageRef.current = null;
      return;
    }
    applyBaseRasterLocks(fabricCanvas, baseImageRef);
  }, []);

  const setViewportToFit = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    const image = baseImageRef.current;
    const element = canvasElementRef.current;
    if (!fabricCanvas || !image || !element) return;

    const layoutContainer = getCanvasLayoutContainer(element);
    const containerWidth = layoutContainer?.clientWidth ?? element.clientWidth;
    const containerHeight = layoutContainer?.clientHeight ?? element.clientHeight;

    if (containerWidth <= 0 || containerHeight <= 0) return;

    historyRef.current.isApplying = true;
    try {
      // Identity viewport: scaling is applied to the base image object (stable with Fabric 7 + DPR).
      fabricCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      fabricCanvas.setDimensions({ width: containerWidth, height: containerHeight });

      const viewportWidth = fabricCanvas.getWidth();
      const viewportHeight = fabricCanvas.getHeight();

      const elementSource = image.getElement();
      const naturalWidth =
        elementSource instanceof HTMLImageElement
          ? elementSource.naturalWidth || image.width || 1
          : image.width || 1;
      const naturalHeight =
        elementSource instanceof HTMLImageElement
          ? elementSource.naturalHeight || image.height || 1
          : image.height || 1;

      const scale = Math.max(viewportWidth / naturalWidth, viewportHeight / naturalHeight);

      image.set({
        originX: 'center',
        originY: 'center',
        left: viewportWidth / 2,
        top: viewportHeight / 2,
        scaleX: scale,
        scaleY: scale,
      });
      image.setCoords();
      fabricCanvas.sendObjectToBack(image);

      lastAppliedLayoutSizeRef.current = {
        width: Math.round(containerWidth),
        height: Math.round(containerHeight),
      };

      setZoom(fabricCanvas.getZoom());
      fabricCanvas.calcOffset();
      fabricCanvas.requestRenderAll();
    } finally {
      historyRef.current.isApplying = false;
      if (historyRef.current.snapshots.length === 0) {
        pushHistorySnapshot();
      }
    }
  }, [pushHistorySnapshot]);

  setViewportToFitRef.current = setViewportToFit;

  // Refit when the layout container resizes (drawer width, etc.). Debounce and skip
  // duplicate sizes so we do not reset the base image mid-stroke (which shifts content).
  useEffect(() => {
    if (!isReady) return;

    const element = canvasElementRef.current;
    if (!element) return;

    const layoutContainer = getCanvasLayoutContainer(element);
    if (!layoutContainer) return;

    const scheduleResizeFit = () => {
      if (layoutResizeTimerRef.current !== null) {
        clearTimeout(layoutResizeTimerRef.current);
      }
      layoutResizeTimerRef.current = setTimeout(() => {
        layoutResizeTimerRef.current = null;
        const el = canvasElementRef.current;
        if (!el) return;
        const layout = getCanvasLayoutContainer(el);
        if (!layout) return;
        const nextWidth = Math.round(layout.clientWidth);
        const nextHeight = Math.round(layout.clientHeight);
        if (nextWidth <= 0 || nextHeight <= 0) return;

        const last = lastAppliedLayoutSizeRef.current;
        if (nextWidth === last.width && nextHeight === last.height) {
          return;
        }

        if (pointerDownOnCanvasRef.current) {
          deferredLayoutRefitRef.current = true;
          return;
        }

        setViewportToFit();
      }, LAYOUT_RESIZE_DEBOUNCE_MS);
    };

    const resizeObserver = new ResizeObserver(() => scheduleResizeFit());
    resizeObserver.observe(layoutContainer);

    return () => {
      resizeObserver.disconnect();
      if (layoutResizeTimerRef.current !== null) {
        clearTimeout(layoutResizeTimerRef.current);
        layoutResizeTimerRef.current = null;
      }
    };
  }, [isReady, setViewportToFit]);

  const setZoomToPoint = useCallback((nextZoom: number, point: { x: number; y: number }) => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, nextZoom));
    fabricCanvas.zoomToPoint(new Point(point.x, point.y), clamped);
    setZoom(fabricCanvas.getZoom());
    fabricCanvas.requestRenderAll();
  }, []);

  const setZoomToCenter = useCallback((nextZoom: number) => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;
    const center = fabricCanvas.getCenterPoint();
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, nextZoom));
    fabricCanvas.zoomToPoint(new Point(center.x, center.y), clamped);
    setZoom(fabricCanvas.getZoom());
    fabricCanvas.requestRenderAll();
  }, []);

  const setTool = useCallback((tool: AnnotationTool) => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;

    activeToolRef.current = tool;
    setActiveTool(tool);

    // Reset canvas interactions.
    fabricCanvas.isDrawingMode = false;
    fabricCanvas.selection = tool === 'select';
    for (const object of fabricCanvas.getObjects()) {
      if (object === baseImageRef.current) continue;
      object.selectable = tool === 'select';
      object.evented = tool === 'select';
    }

    // `baseImageRef` can desync from the live canvas object; always re-lock the bottom raster.
    applyBaseRasterLocks(fabricCanvas, baseImageRef);

    if (tool === 'pencil') {
      fabricCanvas.isDrawingMode = true;
      if (fabricCanvas.freeDrawingBrush) {
        fabricCanvas.freeDrawingBrush.width = PENCIL_STROKE_WIDTH;
        fabricCanvas.freeDrawingBrush.color = annotationColorRef.current;
      }
    }

    fabricCanvas.defaultCursor = tool === 'select' ? 'grab' : 'crosshair';
  }, []);

  setToolRef.current = setTool;

  const undo = useCallback(async () => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;
    const { index } = historyRef.current;
    if (index <= 0) return;

    historyRef.current.isApplying = true;
    historyRef.current.index = index - 1;
    const next = historyRef.current.snapshots[historyRef.current.index];
    await fabricCanvas.loadFromJSON(next);
    syncBaseImageRefAfterLoad();
    fabricCanvas.requestRenderAll();
    historyRef.current.isApplying = false;
    updateUndoRedoState();
  }, [syncBaseImageRefAfterLoad, updateUndoRedoState]);

  const redo = useCallback(async () => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;
    const { snapshots, index } = historyRef.current;
    if (index < 0 || index >= snapshots.length - 1) return;

    historyRef.current.isApplying = true;
    historyRef.current.index = index + 1;
    const next = historyRef.current.snapshots[historyRef.current.index];
    await fabricCanvas.loadFromJSON(next);
    syncBaseImageRefAfterLoad();
    fabricCanvas.requestRenderAll();
    historyRef.current.isApplying = false;
    updateUndoRedoState();
  }, [syncBaseImageRefAfterLoad, updateUndoRedoState]);

  const exportAsBlob = useCallback(
    async (options?: { mimeType?: string; quality?: number }): Promise<Blob> => {
      const fabricCanvas = fabricCanvasRef.current;
      if (!fabricCanvas) {
        throw new Error('Canvas not ready');
      }

      const mimeType = options?.mimeType ?? 'image/png';
      const quality = options?.quality ?? 1;
      const format = mimeType === 'image/jpeg' ? 'jpeg' : 'png';

      // Use Fabric's `toBlob` (via `toCanvasElement`) so all layers export correctly; raw
      // `getElement().toBlob()` can miss content on Fabric 7's multi-buffer canvas.
      let blob: Blob | null;
      try {
        blob = await fabricCanvas.toBlob({ format, quality, multiplier: 1 });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (/tainted/i.test(message)) {
          throw new Error(
            'This drawing cannot be exported because the image was loaded without cross-origin (CORS) permission. Allow CORS on the CDN for this app origin, then reload the page.'
          );
        }
        throw error;
      }
      if (!blob) {
        throw new Error('Failed to export image');
      }
      return blob;
    },
    []
  );

  // Tool drawing handlers for shape-like tools.
  useEffect(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;

    const handleMouseDown = (event: TPointerEventInfo) => {
      const tool = activeToolRef.current;
      if (tool === 'pencil') return;

      if (tool === 'select') {
        // Pan when dragging on an empty area (no target, or target is the locked base image).
        const { target } = event;
        if (!target || target === baseImageRef.current) {
          const { x, y } = getClientPoint(event.e);
          panStateRef.current = { isPanning: true, lastX: x, lastY: y };
          fabricCanvas.defaultCursor = 'grabbing';
        }
        return;
      }

      fabricCanvas.calcOffset();
      const pointer = scenePointFromFabricEvent(fabricCanvas, event);
      drawingStateRef.current.isDrawing = true;
      drawingStateRef.current.startX = pointer.x;
      drawingStateRef.current.startY = pointer.y;

      if (tool === 'text') {
        const text = new IText('Text', {
          left: pointer.x,
          top: pointer.y,
          fontSize: 24,
          fill: annotationColorRef.current,
        });
        fabricCanvas.add(text);
        fabricCanvas.setActiveObject(text);
        text.enterEditing();
        // `IText` stores the underlying textarea internally; focus on next tick.
        setTimeout(() => text.hiddenTextarea?.focus(), 0);
        drawingStateRef.current.isDrawing = false;
        drawingStateRef.current.object = null;
        return;
      }

      if (tool === 'line' || tool === 'arrow') {
        const line = new Line([pointer.x, pointer.y, pointer.x, pointer.y], {
          stroke: annotationColorRef.current,
          strokeWidth: PENCIL_STROKE_WIDTH,
          selectable: false,
          evented: false,
        });
        drawingStateRef.current.object = line;
        fabricCanvas.add(line);
        return;
      }

      if (tool === 'rect') {
        const rect = new Rect({
          left: pointer.x,
          top: pointer.y,
          width: 1,
          height: 1,
          fill: 'transparent',
          stroke: annotationColorRef.current,
          strokeWidth: PENCIL_STROKE_WIDTH,
          selectable: false,
          evented: false,
        });
        drawingStateRef.current.object = rect;
        fabricCanvas.add(rect);
        return;
      }

      if (tool === 'ellipse') {
        const circle = new Circle({
          left: pointer.x,
          top: pointer.y,
          radius: 1,
          fill: 'transparent',
          stroke: annotationColorRef.current,
          strokeWidth: PENCIL_STROKE_WIDTH,
          selectable: false,
          evented: false,
        });
        drawingStateRef.current.object = circle;
        fabricCanvas.add(circle);
      }
    };

    const handleMouseMove = (event: TPointerEventInfo) => {
      if (panStateRef.current.isPanning) {
        const { x, y } = getClientPoint(event.e);
        const dx = x - panStateRef.current.lastX;
        const dy = y - panStateRef.current.lastY;
        panStateRef.current.lastX = x;
        panStateRef.current.lastY = y;
        const vpt = fabricCanvas.viewportTransform;
        vpt[4] += dx;
        vpt[5] += dy;
        fabricCanvas.setViewportTransform(vpt);
        return;
      }

      if (!drawingStateRef.current.isDrawing) return;

      const tool = activeToolRef.current;
      if (tool === 'select' || tool === 'pencil' || tool === 'text') return;

      fabricCanvas.calcOffset();
      const pointer = scenePointFromFabricEvent(fabricCanvas, event);
      const { startX, startY, object } = drawingStateRef.current;
      if (!object) return;

      if (tool === 'line' || tool === 'arrow') {
        const line = object as Line;
        line.set({ x2: pointer.x, y2: pointer.y });
        fabricCanvas.requestRenderAll();
        return;
      }

      if (tool === 'rect') {
        const rect = object as Rect;
        const width = Math.abs(pointer.x - startX);
        const height = Math.abs(pointer.y - startY);
        rect.set({
          left: Math.min(pointer.x, startX),
          top: Math.min(pointer.y, startY),
          width,
          height,
        });
        fabricCanvas.requestRenderAll();
        return;
      }

      if (tool === 'ellipse') {
        const circle = object as Circle;
        const radius = Math.max(Math.abs(pointer.x - startX), Math.abs(pointer.y - startY)) / 2;
        circle.set({
          left: Math.min(pointer.x, startX),
          top: Math.min(pointer.y, startY),
          radius,
        });
        fabricCanvas.requestRenderAll();
      }
    };

    const handleMouseUp = () => {
      if (panStateRef.current.isPanning) {
        panStateRef.current.isPanning = false;
        fabricCanvas.defaultCursor = 'grab';
        return;
      }

      if (!drawingStateRef.current.isDrawing) return;
      drawingStateRef.current.isDrawing = false;

      const tool = activeToolRef.current;
      if (tool === 'arrow') {
        const line = drawingStateRef.current.object as {
          x1?: number;
          y1?: number;
          x2?: number;
          y2?: number;
        } | null;
        if (line) {
          const x1 = line.x1 ?? 0;
          const y1 = line.y1 ?? 0;
          const x2 = line.x2 ?? 0;
          const y2 = line.y2 ?? 0;

          const angle = Math.atan2(y2 - y1, x2 - x1);
          const headLength = 16;
          const triangle = new Triangle({
            left: x2,
            top: y2,
            originX: 'center',
            originY: 'center',
            angle: (angle * 180) / Math.PI + 90,
            width: headLength,
            height: headLength,
            fill: annotationColorRef.current,
            selectable: false,
            evented: false,
          });
          fabricCanvas.add(triangle);
        }
      }

      drawingStateRef.current.object = null;
    };

    fabricCanvas.on('mouse:down', handleMouseDown);
    fabricCanvas.on('mouse:move', handleMouseMove);
    fabricCanvas.on('mouse:up', handleMouseUp);

    return () => {
      fabricCanvas.off('mouse:down', handleMouseDown);
      fabricCanvas.off('mouse:move', handleMouseMove);
      fabricCanvas.off('mouse:up', handleMouseUp);
    };
  }, []);

  const api = useMemo(
    () => ({
      canvasElementRef,
      fabricCanvasRef,
      isReady,
      loadError,
      zoom,
      activeTool,
      canUndo,
      canRedo,
      annotationColor: pickedAnnotationColor,
      setAnnotationColor,
      setCanvasElement,
      setViewportToFit,
      setZoomToPoint,
      setZoomToCenter,
      setTool,
      undo,
      redo,
      exportAsBlob,
    }),
    [
      activeTool,
      pickedAnnotationColor,
      canRedo,
      canUndo,
      isReady,
      loadError,
      redo,
      exportAsBlob,
      setAnnotationColor,
      setCanvasElement,
      setTool,
      setViewportToFit,
      setZoomToCenter,
      setZoomToPoint,
      undo,
      zoom,
    ]
  );

  return api;
};
